import type * as am5 from '@amcharts/amcharts5';
import type * as am5xy from '@amcharts/amcharts5/xy';

import { ActivatedRoute, Router } from '@angular/router';
import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subject, Subscription, catchError, finalize, forkJoin, map, of } from 'rxjs';

import { ChartService } from '../../services/chart.service';
import { Comment } from '../../models/comment.model';
import { CommonService } from 'src/app/services/common.service';
import { ComponentNames } from '../../shared/constants/componentNames.constants';
import { SensorRecord } from '../../models/sensorRecord.model';
import { OnZoom } from '../chartBase/onZoom.interface';
import { IMapComponent } from '../chartBase/mapComponent.interface';
import { Note } from '../../models/note.model';
import { NoteService } from '../../services/note.service';
import { OrganizationLevel } from '../../models/organizationLevel.model';
import { ParameterLookup } from 'src/app/models/parameter.model';
import { NodeSensorRecordsRequestModel } from 'src/app/models/nodeRecords.model';
import { ResizedEvent } from 'angular-resize-event-package';
import { RouterStateSnapshot } from '@angular/router';
import { Sensor } from '../../models/sensor.model';
import { SensorAssignment } from '../../models/sensorAssignment.model';
import { SensorAssignmentService } from 'src/app/services/sensorAssignment.service';
import { SensorRecordService } from '../../services/sensorRecord.service';
import { TaskAssignmentExposureContribution } from '../../models/taskAssignment.model';
import { TaskAssignmentService } from 'src/app/services/taskAssignment.service';
import type { VgApiService } from '@videogular/ngx-videogular/core';
import { VideoResource } from '../../models/videoResource.model';
import { ParameterConstants } from '../../shared/constants/parameter.constants';
import { globalConst } from 'src/app/shared/constants/global.constants';
import { safetyItems } from 'src/app/shared/constants/safetyItems.constants';
import { SampleService } from 'src/app/services/sample.service';
import { Sample } from 'src/app/models/sample.model';
import { sampleDetailsRouteConfig } from 'src/app/shared/constants/sampleDetailsRouteConfig.constants';
import { ILabResultDialogData, LabResultsDialogComponent } from 'src/app/modals/lab-results-dialog/lab-results-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { GravimetricResultsComponent } from 'src/app/common-components/sample-details/gravimetric-results/gravimetric-results.component';
import { ToastService } from 'src/app/services/toast.service';
import { SampleStatusEnum } from 'src/app/enums/sampleStatus.enum';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { DocumentType } from 'src/app/enums/documentType.enum';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { getImgSrc } from 'src/app/_helpers/attachment-helper';
import { EmailStatus } from 'src/app/enums/emailStatus.enum';
import { INodeAssignmentFormDialogData, NodeAssignmentDialogComponent } from 'src/app/modals/node-assignment-dialog/node-assignment-dialog.component';
import { ParameterService } from 'src/app/services/parameter.service';
import { Lookup } from 'src/app/models/lookup.model';
import { MonitoringReasonTypeEnum } from 'src/app/enums/monitoringReasonType.enum';

import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
@Component({
  standalone: false,
  selector: 'app-sample-result',
  templateUrl: './sample-result.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sample-result.component.scss', '../sample-details/sample-details.component.scss'],
})
export class SampleResultComponent implements OnInit, AfterViewInit,
  AfterViewChecked, OnDestroy, OnZoom, IMapComponent {

  searchedText: any;
  sensors: any[] | Sensor[];
  chartSlider: am5.DataItem<am5xy.IDateAxisDataItem>;
  @ViewChild(GravimetricResultsComponent) gravimetericResults: GravimetricResultsComponent;

  readonly CHART_DIV_ID = 'sample-details-chart';
  readonly CHART_VIDEO_DIV_ID = 'sample-details-video-chart';
  activateEndpoint: string;
  zIndex = 0;
  chart: am5xy.XYChart;
  chartDetails: am5xy.XYChart;
  selectedTab = 1;
  isVideoChartHidden = false;
  videoChart: am5xy.XYChart;
  videoChartSlider: am5.DataItem<am5xy.IDateAxisDataItem>;
  videoResources: VideoResource[];
  vgApi: VgApiService;
  videoDisposers: am5.IDisposer[] = [];
  videoResource: VideoResource;
  shouldCreateSliderButtonEvents = false;
  subscriptions: Subscription[] = [];
  skipRangeTimeUpdate = false;
  skipTrackingAction = false;
  triggerPopulateAnalyteForm = false;
  resumeVideoPlay = false;
  allSensorRecords: any = [];
  comments: Comment[] = [];
  state: RouterStateSnapshot;
  isVideoShow = false;
  disposer: am5.IDisposer;
  initDisposer: am5.IDisposer;
  analyticsDisposer: am5.IDisposer;
  analyticsVerticalDisposer: am5.IDisposer;
  sensorAssignmentData: SensorAssignment;
  taskAssignmentExposureData: TaskAssignmentExposureContribution[];

  taskAssignmentDataSource: any;
  taskAssignmentColumnsToDisplay = ['taskName', 'taskGroupName', 'locations', 'actions'];
  taskAssignmentColumnsToDisplayStr = ['Task', 'Task Group', 'Location', 'Time on Shift %', 'Mass Contribution %', 'PPE', 'Actions'];
  taskAssignmentColumnsToDisplayWithExpand = [...this.taskAssignmentColumnsToDisplay, 'timeOnTaskPercentage', 'massContributionPercentage', 'ppeAssignments', 'Actions'];
  taskAssignmentExposureExpandedElement: TaskAssignmentExposureContribution | null;

  refreshUI: Subject<any>;
  clearNotes: Subscription;
  tasksAssignmentNotes: Note[];
  selectedParameter: ParameterLookup;
  parameters: Lookup[] = [];
  selectedOrg: OrganizationLevel;
  sensorAssignmentId: string;
  sampleData: Sample;

  /** True when the sample status is non-editable (Approved, ApprovedLetterSent, Void, VoidLetterSent) */
  get isNonEditableStatus(): boolean {
    if (!this.sampleData) return true;
    return [
      SampleStatusEnum.Approved,
      SampleStatusEnum.ApprovedLetterSent,
      SampleStatusEnum.Void,
      SampleStatusEnum.VoidLetterSent
    ].includes(this.sampleData.sampleStatus);
  }

  showChartFilter = false;
  tasksAssignmentRanges: Record<string, am5.DataItem<am5xy.IDateAxisDataItem>[]> = {};
  tasksAssignmentNotesRanges: Record<string, am5.DataItem<am5xy.IDateAxisDataItem>> = {};
  taskRangeVisibility = true;
  colorSet: am5.ColorSet | null = null;
  private am5Ns: typeof import('@amcharts/amcharts5') | null = null;
  componentName = ComponentNames.sampleDetails;
  taskColor: Record<string, am5.Color> = {};
  taskSelectColor: Record<string, am5.Color> = {};

  selectedTaskName = '';
  loadCommentsAndNotes = true;
  loadingAssessments = true;
  showAnalyteForm = false;
  pageLoad = true;
  safetyItemsList = safetyItems;
  tabs: { routerLink: string; title: string; }[];
  sampleTypeEnum = SampleTypeEnum;
  labAttachments: FileAttachment[];
  reportAttachments: FileAttachment[];
  emailStatusEnum = EmailStatus;

  get hasBlankCorrectionApplied(): boolean {
    return this.sampleData?.ihLabResults?.some(r => r.blankCorrectionApplied) ?? false;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sensorRecordService: SensorRecordService,
    private chartService: ChartService,
    private zone: NgZone,
    private noteService: NoteService,
    private cd: ChangeDetectorRef,
    private commonService: CommonService,
    private taskAssignmentService: TaskAssignmentService,
    private sensorAssignmentService: SensorAssignmentService,
    private sampleService: SampleService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private parameterService: ParameterService
  ) {
    this.refreshUI = new Subject();
  }

  clearNotesRanges() {
    if (this.tasksAssignmentNotes) {
      this.tasksAssignmentNotes.forEach((resource) => {
        this.tasksAssignmentNotesRanges[resource.taskId]?.get('axisFill')?.set('visible', false);
      });
    }
  }

  onClickedOutside() {
    if (this.showChartFilter) {
      this.showChartFilter = false;
    }
  }

  setSelectedOrg(selectedOrg: OrganizationLevel) {
    this.selectedOrg = selectedOrg;
    if (this.selectedParameter && this.pageLoad === false) {
      this.sampleRecordInfo();
    }
  }

  setSelectedParameter(selectedParameter: ParameterLookup) {
    this.selectedParameter = selectedParameter;
    if (this.selectedParameter && this.pageLoad === false) {
      this.sampleRecordInfo();
    }
  }


  onAnalyticsHorizontalZoom() {
  }

  onAnalyticsVerticalZoom() {
  }

  ngAfterViewChecked(): void {
    this.cd.detectChanges();
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  ngOnInit(): void {
    const sampleId = this.route.snapshot.params.id;
    this.subscriptions.push(
      this.parameterService.getAllExcludingNotificationOnly().subscribe(params => {
        this.parameters = params;
      })
    );
    this.loadHeaderData();
    this.getSampleDetails(sampleId);
    this.tabs = sampleDetailsRouteConfig(sampleId.toString());

    this.subscriptions.push(this.refreshUI.subscribe((data) => {
      if (this.chart) this.chartService.disposeChartData(this.chart);
      this.sampleRecordInfo();
    }));
  }

  setAnalyte(parameter: any) {
    this.selectedParameter = parameter;
    this.commonService.setSelectedParameter(this.selectedParameter);
  }

  getSampleDetails(sampleId: string) {
    this.sampleService.getSampleById(sampleId).subscribe({
      next: (result) => {
        this.sampleData = result;
        this.cd.detectChanges();
        this.gravimetericResults.setFilteredRisks();
        this.sampleData.monitoringReasonName = MonitoringReasonTypeEnum[this.sampleData.monitoringReason] ?? '-';
        if (this.sampleData.sensorAssignmentId) {
          this.sensorAssignmentId = this.sampleData.sensorAssignmentId;
          this.loadSampleDetails(this.sensorAssignmentId);
        } else {
          this.loadCommentsAndNotes = false;
          this.loadingAssessments = false;
        };
        const attachments = this.sampleData.fileAttachments.map(res => ({ ...res, imgSrc: getImgSrc(res?.fileType) }))
        this.labAttachments = attachments.filter(fa => fa.documentType === DocumentType.LabResult);
        this.reportAttachments = attachments.filter(fa => fa.documentType === DocumentType.Report);
      },
      error: (error) => {
        this.toastService.showError(error);
      }
    })
  }

  sortExposureContributionsEmit(exposureContributionsSort: any) {
    let columnName = 'taskName';
    if (exposureContributionsSort.active === 'Task Group') {
      columnName = 'taskGroupName';
    } else if (exposureContributionsSort.active === 'Time on Shift') {
      columnName = 'timeOnShiftPercent';
    } else if (exposureContributionsSort.active === 'Mass Contribution') {
      columnName = 'massContributionPercentage';
    }
    this.taskAssignmentDataSource = this.taskAssignmentDataSource.sort((a, b) => {
      const aValue = (a as any)[columnName];
      const bValue = (b as any)[columnName];
      return (aValue < bValue ? -1 : 1) * (exposureContributionsSort.direction === 'asc' ? 1 : -1);
    });
  }

  private loadTaskAssignments(taskAssignmentExposureData) {
    this.tasksAssignmentNotes = [];
    if (taskAssignmentExposureData) {
      const assignments = taskAssignmentExposureData.flatMap(taskAssignment => taskAssignment.taskAssignments);
      const noteRequests = assignments.map(assignment => this.getNotes(assignment.id));
      forkJoin(noteRequests).pipe(finalize(() => this.loadingAssessments = false)).subscribe();

      const result = [];
      const seenTaskIds = new Set<string>();
      taskAssignmentExposureData.forEach((task, index) => {
        const taskColor = this.colorSet?.getIndex(index);
        if (taskColor && this.am5Ns) {
          this.taskColor[task.taskName] = taskColor;
          this.taskSelectColor[task.taskName] = this.am5Ns.Color.lighten(taskColor, 0.7);
        }
        if (!seenTaskIds.has(task.taskId)) {
          seenTaskIds.add(task.taskId);
          result.push(task);
          task.visible = true;
        }
      });

      this.taskAssignmentExposureData = taskAssignmentExposureData;
      taskAssignmentExposureData = result;
      this.taskAssignmentDataSource = taskAssignmentExposureData;
      this.taskAssignmentDataSource = this.commonService.computeMassContribution(this.taskAssignmentDataSource);
    } else {
      this.loadingAssessments = false;
      this.taskAssignmentExposureData = [];
      this.taskAssignmentDataSource = [];
    }
  }

  sampleRecordInfo() {
    setTimeout(() => {
      this.zone.runOutsideAngular(() => {
        setTimeout(async () => {
          this.chart = await this.chartService.createChart(this.CHART_DIV_ID);
          if (!this.colorSet) {
            this.am5Ns = (await this.chartService.getAmCharts5()).am5;
            this.colorSet = this.am5Ns.ColorSet.new(this.chart.root, {});
          }
        }, 50);
      });
      this.createLineChart();
    }, 100);
  }

  endpointAllDetail(parameterName = null, parameterFieldName = null) {
    setTimeout(() => {
      this.zone.runOutsideAngular(async () => {
        this.chartDetails = await this.chartService.createChart('end-point-chart-details');
        this.chartSlider = await this.chartService.createChartRange(this.chartDetails);
        const chartRecords = (this.chart.series.getIndex(0)?.data.values ?? []) as SensorRecord[];
        await this.chartService.drawSensorRecordChart(this.chartDetails, chartRecords, [this.selectedParameter.parameterType],
          this, this.comments, null, this.refreshUI, this.taskAssignmentExposureData, this.componentName, false, parameterName, parameterFieldName);
        this.videoChart = await this.chartService.createChart(this.CHART_VIDEO_DIV_ID);
        this.videoChartSlider = await this.chartService.createChartSlider(this.videoChart, this);
        this.isVideoChartHidden = !this.videoResources || this.videoResources.length === 0;
        this.cd.detectChanges();
        await this.chartService.drawVideoChart(this.videoChart, this.videoResources);

        this.createChartZoomEvent();

        this.chartService.disposeEvent(this.initDisposer);
        this.initDisposer = this.zone.runOutsideAngular(() => {
          return this.chartService.createChartInitialZoomEvent(this.chartDetails, this, false);
        });

        if (this.videoResources && this.videoResources.length > 0) {
          this.videoResource = this.videoResources[0];
          if (this.vgApi) {
            this.videoDisposers = this.chartService.createChartSliderButtonEvents(this.videoChartSlider, this.videoResource.timestampStart, this.videoChart, this.videoDisposers, this);
            this.vgApi.currentTime = 0;
          } else {
            this.shouldCreateSliderButtonEvents = true;
          }
        } else {
          this.videoResource = null;
        }
      });
    }, 400);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
    if (this.clearNotes) {
      this.clearNotes.unsubscribe();
    }

    this.zone.runOutsideAngular(() => {
      this.chartService.disposeEvent(this.disposer);
      this.chartService.disposeEvent(this.initDisposer);
      this.chartService.disposeEvent(this.analyticsDisposer);
      this.chartService.disposeEvent(this.analyticsVerticalDisposer);
      if (this.chart) {
        this.chartService.disposeChartData(this.chart);
        this.chart.dispose();
      }
      if (this.videoChart) {
        this.chartService.disposeChartData(this.videoChart);
        this.videoChart.dispose();
        this.videoChartSlider = null;
        this.chartSlider = null;
      }
    });
  }

  clearCommentRanges() {
    if (this.tasksAssignmentNotes) {
      this.tasksAssignmentNotes.forEach((resource) => {
        this.tasksAssignmentNotesRanges[resource.taskId]?.get('axisFill')?.set('visible', false);
      });
    }
    this.chartService.clearCommentRanges();
  }

  clearTaskRanges() {
    if (this.taskAssignmentExposureData) {
      this.taskAssignmentExposureData.forEach((resource) => {
        if (this.tasksAssignmentRanges[resource.taskId]) {
          this.tasksAssignmentRanges[resource.taskId].forEach(assignment => {
            assignment.get('axisFill')?.set('visible', false);
          });
        }
      });
      this.taskRangeVisibility = true;
      this.selectedTaskName = '';
    }
  }

  createChartZoomEvent() {
    this.chartService.disposeEvent(this.disposer);
    this.disposer = this.zone.runOutsideAngular(() => {
      return this.chartService.createChartZoomEvent(this.chartDetails, this);
    });
    this.chartService.disposeEvent(this.analyticsDisposer);
    this.analyticsDisposer = this.zone.runOutsideAngular(() => {
      return this.chartService.createChartAnalyticsEvent(this.chartDetails, this);
    });
    this.chartService.disposeEvent(this.analyticsVerticalDisposer);
    this.analyticsVerticalDisposer = this.zone.runOutsideAngular(() => {
      return this.chartService.createChartAnalyticsVerticalZoomEvent(this.chartDetails, this);
    });
  }

  onInitialZoom(sensorRecords: SensorRecord[]) {
    if (this.initDisposer && !this.initDisposer.isDisposed() && sensorRecords && sensorRecords.length > 0) {
      this.onDataZoom(sensorRecords);
    }
  }

  onZoom(sensorRecords: SensorRecord[]) {
    if (sensorRecords && sensorRecords.length > 0 && this.initDisposer && !this.initDisposer.isDisposed()) {
      this.initDisposer.dispose();
    }
    this.onDataZoom(sensorRecords);
  }

  onDataZoom(sensorRecords: SensorRecord[]) {
    this.updateMap(sensorRecords);

    const dateAxis = this.chartDetails.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
    const zoomedMin = dateAxis.getPrivate('selectionMin') as number | undefined;
    const zoomedMax = dateAxis.getPrivate('selectionMax') as number | undefined;
    if (this.videoResources && this.videoChart && this.videoResources.length > 0 && zoomedMin && zoomedMax) {
      const videoDateAxis = this.videoChart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
      videoDateAxis.set('min', zoomedMin);
      videoDateAxis.set('max', zoomedMax);
    }
  }

  createLineChart() {
    if (this.sensorAssignmentId && this.selectedParameter && this.selectedOrg) {
      const payload = new NodeSensorRecordsRequestModel();
      const parameters = [this.selectedParameter.parameterType];
      payload.includeVideo = true;
      payload.analyteId = String(this.selectedParameter?.id);
      payload.orgLevelId = this.selectedOrg.id;
      payload.sensorAssignmentId = this.sensorAssignmentId;
      this.loadCommentsAndNotes = true;
      this.loadingAssessments = true;
      forkJoin({
        sampleRecords: this.sensorRecordService.getNodeRecords(payload),
      }).subscribe((data) => {
        this.pageLoad = false;
        this.loadingAssessments = false;
        const sensorRecords = data.sampleRecords.nodeRecords;
        this.comments = [];
        const videoResources = data.sampleRecords.videoResources ? data.sampleRecords.videoResources.reverse() : [];
        const noiseDate = new Date();
        noiseDate.setMinutes(noiseDate.getDate() - 500);
        let parameterName: string = null;
        if (sensorRecords) {
          sensorRecords.forEach(
            record => {
              record.endPointId = this.sensorAssignmentData?.endPointId;
              record.timestamp *= 1000;

              if (record?.value != null) {
                parameterName = this.selectedParameter.name;
                record[ParameterConstants.parameterFieldName] = record.value;
              }
            });
        }
        videoResources.forEach(
          resource => {
            resource.timestampStart *= 1000;
            resource.timestampEnd *= 1000;
          });
        this.videoResources = videoResources;
        this.allSensorRecords = sensorRecords;
        if (this.comments && Array.isArray(this.comments)) {
          this.comments.sort((x, y) => -(x.fromDate - y.toDate));
        }

        this.zone.runOutsideAngular(async () => {
          await this.chartService.drawSensorRecordChart(this.chart, sensorRecords, parameters,
            this, this.comments, null, this.refreshUI, this.taskAssignmentExposureData, this.componentName, false, parameterName, ParameterConstants.parameterFieldName);
          this.endpointAllDetail(parameterName, ParameterConstants.parameterFieldName);
          this.loadCommentsAndNotes = false;
        });

      })
    }
  }

  reloadTaskAssignments() {
    this.loadingAssessments = true;
    this.taskAssignmentService.getExposureContributions(this.sensorAssignmentId, this.selectedParameter.id).subscribe((data) => {
      this.loadTaskAssignments(data);
    }, error => {
      this.loadingAssessments = false;
    });
  }

  onResizedFn(event) {
    this.onResized(event?.event, event?.chart, event?.chartDivId);
  }

  onResized(event: ResizedEvent, chart: am5xy.XYChart, chartDivId: string) {
    this.chartService.onSampleResized(chart, event, chartDivId);
    setTimeout(() => {
      if (chart) {
        const keys = Object.keys(this.tasksAssignmentNotesRanges);
        const dateAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
        const axisHeight = dateAxis.height();
        keys.forEach((key) => {
          this.tasksAssignmentNotesRanges[key]?.get('bullet')?.get('sprite')?.set('y', -(axisHeight > 0 ? (axisHeight - 52) : 100));
        });
      }
    });
  }

  onVideoResized(event: ResizedEvent) {
    this.chartService.onResized(this.videoChart, event, this.CHART_VIDEO_DIV_ID);
  }

  updateMap(sensorRecords: SensorRecord[], shouldRecordBulletClick: boolean = false) {

  }

  onPlayerReady(api: VgApiService) {
    this.vgApi = api;

    if (this.shouldCreateSliderButtonEvents) {
      this.zone.runOutsideAngular(() => {
        this.videoDisposers = this.chartService.createChartSliderButtonEvents(this.videoChartSlider, this.videoResource.timestampStart, this.videoChart, this.videoDisposers, this);
      });
    }

    this.subscriptions.push(api.subscriptions.timeUpdate.subscribe(event => {
      if (!this.videoResource || !this.videoResource.timestampStart) {
        return;
      }
      let currentTime = this.videoResource.timestampStart + event.srcElement.currentTime * 1000;
      currentTime = Math.max(this.videoResource.timestampStart, currentTime);
      currentTime = Math.min(this.videoResource.timestampEnd, currentTime);

      if (!this.skipRangeTimeUpdate) {
        this.videoChartSlider.set('value', currentTime);
        this.chartSlider.set('value', currentTime);
      }

      const videoDateAxis = this.videoChart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
      const videoAxisMin = videoDateAxis.get('min');
      const videoAxisMax = videoDateAxis.get('max');
      if (videoAxisMin && videoAxisMin > currentTime) {
        videoDateAxis.set('min', currentTime - 5000);
      }
      if (videoAxisMax && videoAxisMax < currentTime) {
        videoDateAxis.set('max', currentTime + 5000);
      }
    }));

    this.subscriptions.push(api.getDefaultMedia().subscriptions.ended.subscribe(
      this.nextVideo.bind(this)
    ));

    this.subscriptions.push(api.getDefaultMedia().subscriptions.play.subscribe(
      () => this.onVgApiAction(true)
    ));

    this.subscriptions.push(api.getDefaultMedia().subscriptions.pause.subscribe(
      () => this.onVgApiAction(false)
    ));
  }

  onVgApiAction(shouldResume: boolean): void {
    if (this.skipTrackingAction) {
      return;
    }
    this.resumeVideoPlay = shouldResume;
  }

  nextVideo() {
    const index = this.videoResources.indexOf(this.videoResource);
    if (index >= 0 && index < this.videoResources.length - 1) {
      this.videoResource = this.videoResources[index + 1];
      this.isVideoShow = true;
      this.videoDisposers = this.chartService.createChartSliderButtonEvents(this.videoChartSlider,
        this.videoResource.timestampStart, this.videoChart, this.videoDisposers, this);
      setTimeout(() => this.playVideo(), 200);
    }
  }

  playVideo() {
    this.skipTrackingAction = true;
    this.vgApi.play();
    this.resumeVideoPlay = true;
    setTimeout(() => this.skipTrackingAction = false, 50);
  }

  jumpToVideoTime(timestamp: number) {
    if (timestamp === 0) {
      this.skipTrackingAction = true;
      this.vgApi.pause();
      this.resumeVideoPlay = false;
      setTimeout(() => this.skipTrackingAction = false, 50);
      return;
    }
    this.skipTrackingAction = false;

    let resourceUpdated = false;
    if (!this.videoResource.url || timestamp < this.videoResource.timestampStart || timestamp > this.videoResource.timestampEnd) {
      const resource = this.videoResources.find(r => r.timestampStart <= timestamp && r.timestampEnd >= timestamp);

      if (!resource) {
        this.videoResource = new VideoResource();
        this.isVideoShow = false;
        this.chartSlider.set('value', timestamp);
        return;
      }

      this.skipRangeTimeUpdate = true;
      this.videoResource = resource;
      this.isVideoShow = true;
      this.videoDisposers = this.chartService.createChartSliderButtonEvents(this.videoChartSlider, this.videoResource.timestampStart
        , this.videoChart, this.videoDisposers, this, false);
      this.videoChartSlider.set('value', timestamp);
      this.chartSlider.set('value', timestamp);
      resourceUpdated = true;
    }

    if (resourceUpdated) {
      setTimeout(() => this.setVideoTime(timestamp), 200);
    } else {
      this.setVideoTime(timestamp);
    }
  }

  setVideoTime(timestamp: number) {
    const totalTime = this.videoResource.timestampEnd - this.videoResource.timestampStart;
    let timeElapsed = timestamp - this.videoResource.timestampStart;
    timeElapsed = Math.max(0, timeElapsed);
    timeElapsed = Math.min(totalTime, timeElapsed);
    this.vgApi.currentTime = Math.min(this.vgApi.time.total, timeElapsed / 1000);
    this.skipRangeTimeUpdate = false;

    if (this.resumeVideoPlay) {
      this.playVideo();
    }
  }

  private getNotes(id: string | undefined) {
    return this.noteService.getNotes(id).pipe(
      map(notesData => {
        notesData.forEach(note => {
          note.timestampStart *= 1000;
          note.timestampEnd *= 1000;
        });
        this.tasksAssignmentNotes.push(...notesData);
        return notesData;
      }),
      catchError(() => {
        return of([]);
      })
    );
  }

  loadSampleDetails(id: string) {
    this.sensorAssignmentService.getById(id).subscribe((data) => {
      this.sensorAssignmentData = data;
      this.sensorAssignmentData.totalTimeMins = this.sensorAssignmentData?.totalTime ?
        Math.round(this.sensorAssignmentData?.totalTime / 60) : 0;
      this.sampleRecordInfo();
    });
  }

  toggleShowChartFilter() {
    setTimeout(() => {
      this.showChartFilter = !this.showChartFilter;
    }, 200);
  }

  showTaskRangeFn(event) {
    this.showTaskRange(event?.visibility, event?.taskName);
  }

  showTrendLines() {
    this.chartService.hideShowThresholdTrendLines(this.chartDetails);
    this.showChartFilter = false;
  }

  showCumulativeLine() {
    const series = this.chartDetails.series.values.find((s) => s.get('id') === globalConst.cumulativeSumSeries) as am5xy.LineSeries;
    this.chartService.hideShowSeries(series);
    this.showChartFilter = false;
  }

  showTaskRange(visibility = false, taskName: string = null) {
    setTimeout(() => {
      this.showChartFilter = false;
      if (taskName) {
        if (this.selectedTaskName != taskName) {
          visibility = true;
        }
        this.selectedTaskName = taskName;
      }
      const dateAxis = this.chartDetails.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
      if (this.taskAssignmentExposureData) {
        this.taskAssignmentExposureData.forEach((resource) => {
          if (!this.tasksAssignmentRanges[resource.taskId]) {
            this.tasksAssignmentRanges[resource.taskId] = [];
            resource.taskAssignments.forEach((assignment) => {
              const rangeItem = dateAxis.makeDataItem({
                value: assignment.startDate * 1000,
                endValue: assignment.endDate * 1000,
              });
              dateAxis.createAxisRange(rangeItem);
              rangeItem.get('axisFill')?.set('fillOpacity', 0.3);
              rangeItem.get('label')?.set('text', '');
              this.tasksAssignmentRanges[resource.taskId].push(rangeItem);
            });
          }
          this.tasksAssignmentRanges[resource.taskId].forEach(assignment => {
            const taskColor = this.taskColor[resource.taskName];
            assignment.get('label')?.set('fill', taskColor);
            assignment.get('axisFill')?.set('fill', taskColor);
            const rangeVisible = ((resource.taskName === taskName) || (taskName === null)) ? visibility : false;
            assignment.get('axisFill')?.set('visible', rangeVisible);
            assignment.get('label')?.set('visible', rangeVisible);
          });
        });
      }
      this.taskRangeVisibility = !visibility;
      this.chartService.showHideComments(false);
      if (this.tasksAssignmentNotes) {
        this.tasksAssignmentNotes.forEach((resource) => {
          if (this.tasksAssignmentNotesRanges[resource.taskId]) {
            this.tasksAssignmentRanges[resource.taskId].forEach(assignment => {
              assignment.get('axisFill')?.set('visible', false);
              assignment.get('label')?.set('visible', false);
              assignment.get('bullet')?.get('sprite')?.set('visible', false);
            });
          }
        });
      }
    }, 200);
  }

  showNotesRangeFn(event) {
    this.showNotesRange(event?.visibility, event?.taskId);
  }

  hideChartFilterFn() {
    setTimeout(() => {
      this.showChartFilter = false;
    }, 200);
  }

  showCommentRange(comment: Comment) {
    setTimeout(async () => {
      this.showChartFilter = false;
      const dateAxis = this.chartDetails.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
      if (this.tasksAssignmentNotes) {
        for (const resource of this.tasksAssignmentNotes) {
          if (!this.tasksAssignmentNotesRanges[resource.taskId]) {
            this.tasksAssignmentNotesRanges[resource.taskId] = await this.plotBulletsRange(dateAxis, resource.taskId,
              resource.timestampStart, resource.timestampEnd, resource.type);
          }
          const rangeItem = this.tasksAssignmentNotesRanges[resource.taskId];
          rangeItem.get('label')?.set('visible', true);
          rangeItem.get('bullet')?.get('sprite')?.set('visible', true);
        }
      }
      if (this.clearNotes) {
        this.clearNotes.unsubscribe();
      }
      this.clearNotes = this.chartService.clearNotes.subscribe((data) => {
        if (data) {
          this.clearNotesRanges();
        }
      });
      this.taskRangeVisibility = false;
      const id = comment.id;
      const startDate = comment.fromDate * 1000;
      const endDate = comment.toDate * 1000;
      const cKey = startDate + '-' + endDate;
      const keys = Object.keys(this.chartService.commentsRange);
      keys.forEach((key) => {
        const rangeItem = this.chartService.commentsRange[key];
        if (rangeItem) {
          rangeItem.get('bullet')?.get('sprite')?.set('visible', true);
          const isMatch = key == cKey;
          rangeItem.get('axisFill')?.set('fillOpacity', isMatch ? 0.3 : 0);
          rangeItem.get('axisFill')?.set('visible', isMatch);
        }
      });
      const comments = document.querySelectorAll('.comments-notes-div .comment-list');
      for (let i = 0; i < comments.length; i++) {
        (comments[i] as HTMLElement).style.backgroundColor = 'unset';
      }
      const commentHTML = document.getElementById(`c-${id}`);
      if (commentHTML) {
        commentHTML.style.backgroundColor = this.commonService.colors.yellowLight;
      }
    }, 100);
  }

  showNoteRange(note: Note) {
    setTimeout(async () => {
      this.showChartFilter = false;
      const dateAxis = this.chartDetails.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
      if (this.tasksAssignmentNotes) {
        for (const resource of this.tasksAssignmentNotes) {
          if (!this.tasksAssignmentNotesRanges[resource.id]) {
            this.tasksAssignmentNotesRanges[resource.id] = await this.plotBulletsRange(dateAxis, resource.id,
              resource.timestampStart, resource.timestampEnd, resource.type);
          }
          const rangeItem = this.tasksAssignmentNotesRanges[resource.id];
          rangeItem.get('label')?.set('visible', true);
          rangeItem.get('bullet')?.get('sprite')?.set('visible', true);
          rangeItem.get('axisFill')?.set('visible', resource.id === note.id);
        }
      }
      const noteHTML = document.getElementById(`n-${note.id}`);
      if (noteHTML) {
        noteHTML.style.backgroundColor = this.commonService.colors.primaryLight;
      }
      if (this.clearNotes) {
        this.clearNotes.unsubscribe();
      }
      this.clearNotes = this.chartService.clearNotes.subscribe((data) => {
        if (data) {
          this.clearNotesRanges();
        }
      });
      this.taskRangeVisibility = false;
      this.chartService.showHideComments(true);
    }, 100);
  }

  async showNotesRange(visibility = false, id: string = '') {
    this.showChartFilter = false;
    const dateAxis = this.chartDetails.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX>;
    if (this.tasksAssignmentNotes) {
      for (const resource of this.tasksAssignmentNotes) {
        if (!this.tasksAssignmentNotesRanges[resource.id]) {
          this.tasksAssignmentNotesRanges[resource.id] = await this.plotBulletsRange(dateAxis, resource.id,
            resource.timestampStart, resource.timestampEnd, resource.type);
        }
        const rangeItem = this.tasksAssignmentNotesRanges[resource.id];
        const rangeVisible = ((resource.id === id) || (!id)) ? visibility : false;
        rangeItem.get('label')?.set('visible', rangeVisible);
        rangeItem.get('bullet')?.get('sprite')?.set('visible', rangeVisible);
      }
    }
    if (this.clearNotes) {
      this.clearNotes.unsubscribe();
    }
    this.clearNotes = this.chartService.clearNotes.subscribe((data) => {
      if (data) {
        this.clearNotesRanges();
      }
    });
    this.taskRangeVisibility = !visibility;
    this.chartService.showHideComments(visibility);
  }

  /**
   * v5 equivalent of the v4 HTML-labeled task/note range bullet: v5 Labels render plain text
   * (no `label.html`), so the "IH" badge + material icon is composed from a Container holding
   * a RoundedRectangle background and sibling icon/text Labels using material icon font
   * ligatures, mirroring ChartService's createRangeWithBullet/addLabelBulletForIndividualComment.
   */
  private async plotBulletsRange(dateAxis: am5xy.DateAxis<am5xy.AxisRendererX>, id: string, startDate: number, endDate: number, type: number): Promise<am5.DataItem<am5xy.IDateAxisDataItem>> {
    const { am5: am5Ns, am5xy: am5xyNs } = await this.chartService.getAmCharts5();
    const root = dateAxis.root;
    if (startDate - endDate === 0) {
      startDate -= 150000;
      endDate += 150000;
    }

    const rangeItem = dateAxis.makeDataItem({ value: startDate, endValue: endDate });
    dateAxis.createAxisRange(rangeItem);
    rangeItem.get('axisFill')?.setAll({
      fill: am5Ns.color(this.commonService.colors.primary),
      fillOpacity: 0.3,
      visible: false,
    });
    rangeItem.get('label')?.setAll({ text: '', fill: am5Ns.color(this.commonService.colors.primary) });

    const isHighlighted = type === 1 || type === 2;
    const axisHeight = dateAxis.height();
    const badge = am5Ns.Container.new(root, {
      centerX: am5Ns.p50,
      centerY: am5Ns.p50,
      dy: 10,
      y: -(axisHeight > 0 ? (axisHeight - 52) : 100),
      cursorOverStyle: 'pointer',
      layout: root.horizontalLayout,
      paddingTop: isHighlighted ? 4 : 0,
      paddingBottom: isHighlighted ? 4 : 0,
      paddingLeft: isHighlighted ? 8 : 0,
      paddingRight: isHighlighted ? 8 : 0,
      background: isHighlighted
        ? am5Ns.RoundedRectangle.new(root, {
          cornerRadiusTL: 5,
          cornerRadiusTR: 5,
          cornerRadiusBL: 5,
          cornerRadiusBR: 5,
          fill: am5Ns.color(this.commonService.colors.primary),
          stroke: am5Ns.color(this.commonService.colors.black),
          strokeOpacity: 1,
        })
        : undefined,
    });

    const iconText = type === 1 ? 'mic' : type === 2 ? 'play_arrow' : 'mode_comment';
    const iconFill = isHighlighted ? this.commonService.colors.white : this.commonService.colors.primary;
    const iconLabel = am5Ns.Label.new(root, {
      text: iconText,
      fontFamily: isHighlighted ? 'Material Symbols Outlined' : 'Material Icons',
      fill: am5Ns.color(iconFill),
      fontSize: 14,
      centerY: am5Ns.p50,
    });
    const textLabel = am5Ns.Label.new(root, {
      text: isHighlighted ? 'IH -' : 'IH',
      fill: am5Ns.color(this.commonService.colors.white),
      fontSize: 12,
      centerY: am5Ns.p50,
    });
    if (isHighlighted) {
      badge.children.push(textLabel);
      badge.children.push(iconLabel);
    } else {
      badge.children.push(iconLabel);
      badge.children.push(textLabel);
    }

    rangeItem.set('bullet', am5xyNs.AxisBullet.new(root, { sprite: badge, location: 0.5 }));
    badge.events.on('click', this.scrollToCommentNoteById(id));

    return rangeItem;
  }

  generateReport() {
    // Get the report page URL with the returnUrl query parameter
    const reportUrl = this.router.createUrlTree(['/reports', this.sensorAssignmentId]).toString();
    window.open(reportUrl, '_blank');
  }

  private scrollToCommentNoteById(id: string): () => void {
    return () => {
      const comments = document.querySelectorAll('.comments-notes-div .comment-list');
      for (let i = 0; i < comments.length; i++) {
        (comments[i] as HTMLElement).style.backgroundColor = 'unset';
      }
      const keys = Object.keys(this.chartService.commentsRange);
      keys.forEach((key) => {
        const rangeItem = this.chartService.commentsRange[key];
        rangeItem?.get('axisFill')?.set('fillOpacity', 0);
        rangeItem?.get('axisFill')?.set('visible', false);
      });
      if (this.tasksAssignmentNotes) {
        this.tasksAssignmentNotes.forEach((resource) => {
          this.tasksAssignmentNotesRanges[resource.id]?.get('axisFill')?.set('visible', resource.id === id);
        });
      }
      const noteHTML = document.getElementById(`n-${id}`);
      if (noteHTML) {
        noteHTML.style.backgroundColor = this.commonService.colors.primaryLight;
        noteHTML.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
      }
    };
  }

  loadHeaderData() {
    this.subscriptions.push(this.commonService.selectParameterEmitter.subscribe((parameter) => {
      if (parameter) { this.setSelectedParameter(parameter); }
    }));
    this.subscriptions.push(this.commonService.selectedOrgEmitter.subscribe((selectedOrg) => {
      if (selectedOrg) { this.setSelectedOrg(selectedOrg); }
    }));
  }

  downloadAttachment(attachment: FileAttachment) {
    if (attachment.url) {
      const a = document.createElement('a');
      a.href = attachment.url;
      a.download = attachment.name || 'download';
      a.target = '_blank';
      a.click();
    } else {
      this.toastService.displayErrorMessage('No URL available for download', true)
    }
  }

  openLabResultsDialog(entityId: string = this.sampleData.id) {
    const data: ILabResultDialogData = {
      id: entityId ?? '',
      sampleType: this.sampleData.sampleType
    }
    const dialogRef = this.dialog.open(LabResultsDialogComponent, {
      data: data,
      width: '96%',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.getSampleDetails(this.sampleData.id);
      }
    });
  }

  openNodeAssignmentDialog(): void {
    if (!this.sampleData?.endPoint || !this.sampleData?.sensorAssignmentId) {
      this.toastService.displayErrorMessage('No Sensor Assignment available', true);
      return;
    }

    const { endPoint } = this.sampleData;
    const nodeAssignmentModel: INodeAssignmentFormDialogData = {
      id: this.sampleData.sensorAssignmentId,
      nodeId: endPoint.id,
      nodeName: endPoint.name,
      organizationLevelId: endPoint.organizationLevelId,
      isNodeAssignment: true
    };

    this.dialog.open(NodeAssignmentDialogComponent, {
      data: nodeAssignmentModel,
      width: '550px',
      disableClose: false
    });
  }

    public trackById = trackById;
    public trackByIndex = trackByIndex;
}
